import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Slideout, Button, Pill } from './index';
import { useToast } from '../../contexts/ToastContext';
import * as api from '../../services/api';

/**
 * Phase-9 — reusable Promote affordance for any of the four promotable
 * artifact types (Workflow / Agent / Connector / ConnectorGroup).
 *
 * Composition pattern: the artifact detail page renders its own Promote
 * button (gated by `hasPermission('promotion.initiate')`) and on click
 * mounts <PromoteSlideout>. The slideout queries the source Instance's
 * tenant for declared promotion paths, lets the operator pick a target,
 * chooses a conflict-resolution mode, and submits.
 *
 * Props:
 *   isOpen, onClose            — slideout lifecycle
 *   resourceType               — 'WORKFLOW' | 'AGENT' | 'CONNECTOR' | 'CONNECTOR_GROUP'
 *   resourceId                 — UUID of the source artifact
 *   resourceName               — human-readable name (header copy)
 *   sourceInstanceId           — Instance the artifact currently lives in
 *   sourceInstanceName         — for human display
 *   sourceTenantId             — used to load the tenant's Promotion Graph
 *   onSubmitted                — optional callback (PromotionEventDTO) after success
 *
 * UX notes:
 *   - Target dropdown is populated from enabled PromotionPaths whose
 *     fromInstanceId matches sourceInstanceId. Empty state when none.
 *   - Server may auto-execute (returns PROMOTED) or queue for approval
 *     (returns PENDING_APPROVAL). The toast text reflects which happened.
 */
const TIER_TONE = {
  PRODUCTION: 'danger',
  STAGING:    'warning',
  DEV:        'cyan',
  SYSTEM:     'muted',
  CUSTOM:     'muted',
};

const CONFLICT_OPTIONS = [
  { value: 'CREATE_NEW',
    label: 'Create a new copy (recommended)',
    detail: 'If the target already has an artifact with the same name, append a "-promoted-N" suffix and keep both.' },
  { value: 'OVERRIDE',
    label: 'Override the existing target',
    detail: 'Archive the existing target-side artifact, then create the clone with the original name.' },
  { value: 'STOP_FORCE_RENAME',
    label: 'Stop if a name collision exists',
    detail: 'Refuse the promotion when a target-side row has the same name. Rename the source first.' },
];

export function PromoteSlideout({
  isOpen, onClose,
  resourceType, resourceId, resourceName,
  sourceInstanceId, sourceInstanceName,
  sourceTenantId,
  onSubmitted,
}) {
  const [paths, setPaths] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [targetInstanceId, setTargetInstanceId] = useState('');
  const [conflictResolution, setConflictResolution] = useState('CREATE_NEW');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  // Load the tenant's Promotion Graph on open, then filter to enabled
  // edges originating from this artifact's source Instance.
  const reload = useCallback(async () => {
    if (!sourceTenantId) return;
    setLoading(true); setError(null);
    try {
      const res = await api.getPromotionPaths(sourceTenantId);
      if (res && res.ok === false) {
        setError(res.message || res.error || 'Failed to load promotion paths');
        setPaths([]);
      } else {
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        setPaths(Array.isArray(payload) ? payload : []);
      }
    } catch (e) {
      setError(e?.message || String(e));
      setPaths([]);
    } finally {
      setLoading(false);
    }
  }, [sourceTenantId]);

  useEffect(() => {
    if (isOpen) {
      setTargetInstanceId('');
      setConflictResolution('CREATE_NEW');
      setNotes('');
      reload();
    }
  }, [isOpen, reload]);

  // Outbound enabled edges from this artifact's source Instance.
  const outbound = useMemo(() => {
    if (!Array.isArray(paths)) return [];
    return paths.filter(p =>
      p.fromInstanceId === sourceInstanceId && p.enabled !== false);
  }, [paths, sourceInstanceId]);

  const targetMeta = useMemo(() => {
    return outbound.find(p => p.toInstanceId === targetInstanceId);
  }, [outbound, targetInstanceId]);

  const submit = async () => {
    if (!targetInstanceId) return;
    setSubmitting(true);
    try {
      const res = await api.initiatePromotion({
        resourceType,
        sourceResourceId: resourceId,
        targetInstanceId,
        conflictResolution,
        notes: notes || null,
      });
      if (res && res.ok === false) {
        toast.addToast({ tone: 'danger', message: res.message || res.error || 'Promotion failed' });
        return;
      }
      const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
      const queued = payload && payload.status === 'PENDING_APPROVAL';
      toast.addToast({
        tone: 'success',
        message: queued
          ? 'Promotion submitted — pending approval'
          : 'Promotion completed',
      });
      if (onSubmitted) onSubmitted(payload);
      onClose && onClose();
    } catch (e) {
      toast.addToast({ tone: 'danger', message: e?.message || 'Promotion failed' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      header={
        <div>
          <div className="font-semibold text-v2-ink">
            Promote {resourceType.toLowerCase().replace('_', ' ')}
          </div>
          {resourceName && (
            <div className="text-[12px] text-v2-ink-muted mt-0.5">
              "{resourceName}" from {sourceInstanceName || '(unknown source)'}
            </div>
          )}
        </div>
      }
      ariaLabel="Promote artifact"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={submit}
                  disabled={submitting || !targetInstanceId}>
            {submitting ? 'Submitting…' : 'Promote'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-[12px] text-v2-ink-muted">
          Pick a target instance for this {resourceType.toLowerCase().replace('_', ' ')}.
          Only targets declared in your tenant's Promotion Graph appear below.
        </p>

        {loading && paths === null && (
          <div className="text-[12px] text-v2-ink-muted">Loading promotion paths…</div>
        )}

        {error && (
          <div className="text-[12px] text-v2-danger border border-v2-danger rounded-v2-md px-3 py-2 bg-v2-danger-soft">
            {error}
          </div>
        )}

        {!loading && !error && outbound.length === 0 && (
          <div className="text-[12px] border border-v2-border-subtle rounded-v2-md p-3 bg-v2-muted">
            <div className="font-medium text-v2-ink mb-1">
              No promotion paths declared from {sourceInstanceName || 'this instance'}.
            </div>
            <div className="text-v2-ink-muted">
              Ask your Tenant Admin to add one in Multi-Tenancy → Promotion Graph.
            </div>
          </div>
        )}

        {!loading && !error && outbound.length > 0 && (
          <>
            <label className="block">
              <div className="text-[11.5px] uppercase tracking-wider text-v2-ink-muted mb-1">Target instance</div>
              <select
                className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2 py-1.5 text-[13px] bg-v2-surface"
                value={targetInstanceId}
                onChange={(e) => setTargetInstanceId(e.target.value)}
              >
                <option value="">— select target —</option>
                {outbound.map(p => (
                  <option key={p.id} value={p.toInstanceId}>
                    {p.toName} · {p.toTier}{p.toStatus === 'ARCHIVED' ? ' (archived)' : ''}
                  </option>
                ))}
              </select>
              {targetMeta && (
                <div className="mt-1 flex items-center gap-2 text-[11px] text-v2-ink-muted">
                  <Pill tone={TIER_TONE[targetMeta.toTier] || 'muted'} size="sm">{targetMeta.toTier}</Pill>
                  {targetMeta.notes && <span>· {targetMeta.notes}</span>}
                </div>
              )}
            </label>

            <label className="block">
              <div className="text-[11.5px] uppercase tracking-wider text-v2-ink-muted mb-1">If a name collision exists at the target</div>
              <select
                className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2 py-1.5 text-[13px] bg-v2-surface"
                value={conflictResolution}
                onChange={(e) => setConflictResolution(e.target.value)}
              >
                {CONFLICT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <div className="mt-1 text-[11px] text-v2-ink-muted">
                {(CONFLICT_OPTIONS.find(o => o.value === conflictResolution) || {}).detail}
              </div>
            </label>

            <label className="block">
              <div className="text-[11.5px] uppercase tracking-wider text-v2-ink-muted mb-1">Notes (optional)</div>
              <textarea
                className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2 py-1.5 text-[13px] bg-v2-surface"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="What's this promotion for? Goes to the audit trail and the approver's inbox."
              />
            </label>
          </>
        )}
      </div>
    </Slideout>
  );
}

export default PromoteSlideout;
